/**
 * Chaos mutations — the only channel through which anything outside the
 * simulation may change the rules of a live match.
 *
 * Four properties hold here, and each one is load-bearing for a wagered match:
 *
 *   1. NO VARIANT CAN NAME A PLAYER. Every field except `kind` is a `number`,
 *      and that is proved at compile time by `_AllPayloadFieldsAreNumbers`
 *      below — not asserted in a comment, not requested in a prompt. A director
 *      that cannot express "hurt Rodrigo" cannot be prompt-injected into hurting
 *      Rodrigo, cannot be bribed into it, and cannot do it by accident.
 *
 *   2. APPLYING A MUTATION IS DETERMINISTIC. `applyMutation` draws no randomness,
 *      reads no clock, and writes only into `world.chaos`. The same mutation
 *      applied at the same tick to the same world produces the same world.
 *
 *   3. EVERY APPLIED MUTATION IS IN `serializeWorld`, so `worldChecksum` covers
 *      it, so the replay verifier sees it. A mutation invisible to the checksum
 *      is invisible to the verifier, and a payout dispute over a match whose
 *      ruleset changed off the record is unsettleable. Entries are appended and
 *      never removed — an expired mutation stays in the digest forever.
 *
 *   4. AMBIENT MUTATIONS DO NOTHING AT ALL. See `applyMutation`.
 *
 * What is NOT here, and must not be added:
 *
 *   - DAMAGE AMPLIFICATION. Doubling damage turns Monolith's 55-damage ultimate
 *     into a 110 one-shot on 80-HP Volt, which breaks the "TTK 6-10s, rarely a
 *     solo kill from full HP" pillar the whole roster is balanced against
 *     (GAME-DESIGN §3, PRD §8 rule 10 cuts it by name). There is no `damage_amp`
 *     variant, so there is nothing for a future caller to reach: the sim has no
 *     damage multiplier to set, at any layer.
 *   - Anything that ends a round, picks a winner, or moves a specific player.
 *     "Sudden death" is a zone schedule fixed at match start (see `zone.ts`),
 *     never a director power.
 */
import { bodyHalfHeight, isMutationActive } from "./body.js";
import { applyDamage } from "./combat.js";
import { DT, ZONE_HIT_EVENT_INTERVAL_TICKS } from "./constants.js";
import { getMutationFloorY, MUTATION_FLOOR_TOLERANCE } from './map.js';
import { dcos, dsin, msToTicks, PI } from "./math.js";
import { ZONE_SOURCE_ID } from "./zone.js";
/** Tiers, mirroring GAME-DESIGN §4. Ambient events never touch the simulation. */
export const AMBIENT_KINDS = ["fog_roll", "color_shift", "tremor"];
export function isAmbientKind(kind) {
    return AMBIENT_KINDS.includes(kind);
}
// ---------------------------------------------------------------------------
// Runtime validation
// ---------------------------------------------------------------------------
/**
 * Field -> [min, max] per kind, and the EXACT set of fields each kind may carry.
 *
 * The bounds are a second line after the backend's allowlist, because a mutation
 * can also arrive from a replay log — untrusted JSON on disk, replayed months
 * later to settle a dispute. `paramString` reads this same table, so a field
 * that exists here is necessarily in the checksum and a field that is not here
 * is rejected before it can be applied.
 */
export const MUTATION_BOUNDS = {
    low_gravity: { gravityPct: [25, 100], durationMs: [1_000, 120_000] },
    ice_floor: { durationMs: [1_000, 120_000] },
    // `driftSpeed` is a steady-state velocity in u/s and must stay below
    // GROUND_DECEL * DT — 8.0 u/s since the deceleration retune, and the 1.9 cap
    // was set when it was 2.0. Above that bound the ground deceleration no longer
    // balances the conveyor each tick and the drift diverges until the
    // MAX_STEP_XZ backstop catches it — a mutator that fires players off the map.
    // The cap is left where it is: it was tuned for feel, and the headroom it now
    // has over the bound is margin, not slack to spend.
    conveyor_floor: { directionDeg: [0, 360], driftSpeed: [0.25, 1.9], durationMs: [1_000, 120_000] },
    hft_belt_flow: { routeGroup: [0, 2], directionMultiplier: [-1, 1], strengthPct: [100, 200], durationMs: [5_000, 20_000] },
    speed_surge: { speedPct: [100, 160], durationMs: [1_000, 120_000] },
    lava_floor: { dps: [1, 30], durationMs: [1_000, 120_000] },
    cooldown_haste: { hastePct: [1, 60] },
    // Short by design. Disorientation is funny for ten seconds and miserable for
    // ninety, and unlike the other timed mutators there is no counterplay to
    // learn — you either adapt or you do not.
    inverted_controls: { durationMs: [1_000, 30_000] },
    // Bounded well inside "cannot fit where it used to". The maps are built
    // around a 0.5-radius body, so 130% is a squeeze and anything near 170% is a
    // body that can no longer path through its own cover.
    player_scale: { scalePct: [70, 130], durationMs: [1_000, 60_000] },
    fog_roll: { densityPct: [0, 100], durationMs: [1_000, 120_000] },
    color_shift: { hueDeg: [0, 360], durationMs: [1_000, 120_000] },
    tremor: { magnitudePct: [0, 100], durationMs: [1_000, 120_000] },
};
export const MUTATION_KINDS = Object.keys(MUTATION_BOUNDS).sort();
export function isMutationKind(value) {
    return Object.prototype.hasOwnProperty.call(MUTATION_BOUNDS, value);
}
export function validateMutation(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return { ok: false, reason: "not an object" };
    }
    const obj = value;
    const kind = obj["kind"];
    if (typeof kind !== "string" || !isMutationKind(kind)) {
        return { ok: false, reason: `unknown mutation kind ${JSON.stringify(kind)}` };
    }
    const bounds = MUTATION_BOUNDS[kind];
    const allowed = new Set(["kind", ...Object.keys(bounds)]);
    for (const key of Object.keys(obj)) {
        // The important branch. An extra key that is merely ignored is how a player
        // reference travels through a schema nobody thought could carry one.
        if (!allowed.has(key))
            return { ok: false, reason: `unexpected field ${JSON.stringify(key)} on ${kind}` };
    }
    for (const [field, range] of Object.entries(bounds)) {
        const raw = obj[field];
        if (typeof raw !== "number" || !Number.isFinite(raw)) {
            return { ok: false, reason: `${kind}.${field} must be a finite number` };
        }
        const [lo, hi] = range;
        if (raw < lo || raw > hi)
            return { ok: false, reason: `${kind}.${field}=${raw} outside [${lo}, ${hi}]` };
    }
    if (kind === "hft_belt_flow") {
        if (![0, 1, 2].includes(obj["routeGroup"]))
            return { ok: false, reason: "hft_belt_flow.routeGroup must be 0, 1, or 2" };
        if (![-1, 1].includes(obj["directionMultiplier"]))
            return { ok: false, reason: "hft_belt_flow.directionMultiplier must be -1 or 1" };
    }
    return { ok: true };
}
export function isMutation(value) {
    return validateMutation(value).ok;
}
export class MutationError extends Error {
    constructor(message) {
        super(message);
        this.name = "MutationError";
    }
}
export function createChaosState() {
    return { applied: [] };
}
// ---------------------------------------------------------------------------
// Applying
// ---------------------------------------------------------------------------
/**
 * Applies a committed mutation to the world.
 *
 * Called BETWEEN ticks — the room applies a scheduled event before stepping the
 * tick it was scheduled for, so the mutation takes effect from `world.tick + 1`
 * and every input resolved in that tick sees the same ruleset. Applying after
 * the step would give one tick of play a ruleset nobody was announced.
 *
 * Throws on a payload that fails the allowlist. Deliberately loud: silently
 * ignoring a bad mutation makes the live match and its replay diverge, and a
 * divergence discovered during a payout dispute is worse than a crash
 * discovered at the tick it happened.
 */
export function applyMutation(world, mutation) {
    const check = validateMutation(mutation);
    if (!check.ok)
        throw new MutationError(`Rejected mutation: ${check.reason}`);
    // A finished match is inert, the same stance `stepWorld` takes. Placements are
    // decided; the only thing a late mutation could still move is the checksum,
    // which is the one thing a settled match must not move.
    if (world.ended)
        return;
    // GAME-DESIGN §4 rule 4: ambient events are COSMETIC ONLY in a wagered match.
    // Returning before touching any state is what makes that true rather than
    // aspirational — and it also keeps the live room (which never forwards
    // ambient events to the sim) bit-identical to the replay verifier (which
    // replays every recorded `apply` row, ambient included). If an ambient
    // mutation changed one byte of world state, those two would disagree and
    // every replay of a match with fog in it would fail verification.
    if (isAmbientKind(mutation.kind))
        return;
    const startTick = world.tick + 1;
    if (mutation.kind === "cooldown_haste") {
        // One-shot: pull every cooldown in flight toward the present. Writes are
        // independent per ability, so record iteration order cannot affect the
        // result.
        const keep = 1 - mutation.hastePct / 100;
        for (const p of world.players) {
            if (p.phase !== "alive")
                continue;
            for (const abilityId of Object.keys(p.cooldowns)) {
                const until = p.cooldowns[abilityId];
                if (until === undefined || until <= startTick)
                    continue;
                p.cooldowns[abilityId] = startTick + Math.ceil((until - startTick) * keep);
            }
        }
        world.chaos.applied.push({ kind: mutation.kind, tick: startTick, untilTick: startTick, mutation });
        return;
    }
    world.chaos.applied.push({
        kind: mutation.kind,
        tick: startTick,
        untilTick: startTick + msToTicks(mutation.durationMs),
        mutation,
    });
}
/**
 * Owned by `body.ts` so that `combat.ts` can read the body scale without
 * importing this module and closing a cycle. Aliased rather than re-declared:
 * one definition of "in effect".
 */
const isActive = isMutationActive;
/** Everything currently in effect. Exposed for tests and for the room's HUD. */
export function activeMutations(world) {
    return world.chaos.applied.filter((m) => isActive(m, world.tick));
}
// ---------------------------------------------------------------------------
// Per-tick queries — read by `stepWorld`
// ---------------------------------------------------------------------------
/**
 * Gravity scale from active mutations. 1 = normal.
 *
 * Overlapping sources take the STRONGEST rather than multiplying, the same
 * stance `effects.ts` takes on damage reduction: two 50% gravity rolls whose
 * windows overlap must not compound into 25%, which is a different game.
 */
export function chaosGravityMultiplier(world) {
    let mult = 1;
    for (const entry of world.chaos.applied) {
        const m = entry.mutation;
        if (m.kind !== "low_gravity" || !isActive(entry, world.tick))
            continue;
        const v = m.gravityPct / 100;
        if (v < mult)
            mult = v;
    }
    return mult;
}
/**
 * Are movement controls inverted this tick?
 *
 * Boolean rather than a multiplier: two overlapping inversions must not cancel
 * into normal movement, which would be the strangest possible bug to report.
 * Any active `inverted_controls` inverts, however many there are.
 */
export function chaosControlsInverted(world) {
    for (const entry of world.chaos.applied) {
        if (entry.mutation.kind === "inverted_controls" && isActive(entry, world.tick))
            return true;
    }
    return false;
}
/** Move-speed scale from active mutations. Strongest wins; never below 1. */
export function chaosSpeedMultiplier(world) {
    let mult = 1;
    for (const entry of world.chaos.applied) {
        const m = entry.mutation;
        if (m.kind !== "speed_surge" || !isActive(entry, world.tick))
            continue;
        const v = m.speedPct / 100;
        if (v > mult)
            mult = v;
    }
    return mult;
}
function activeFloor(world) {
    let found = null;
    for (const entry of world.chaos.applied) {
        const m = entry.mutation;
        if (m.kind !== "ice_floor" && m.kind !== "conveyor_floor")
            continue;
        if (!isActive(entry, world.tick))
            continue;
        found = m;
    }
    return found;
}
/**
 * The surface the movement step should treat the player as standing on.
 *
 * A chaos floor covers the arena floor and plain platforms. Bounce pads and
 * speed strips keep their authored surface: the map's own mechanics are not
 * something a director gets to overwrite, and a bounce pad that stops bouncing
 * mid-match is a bug report, not a mutator.
 */
export function chaosSurface(world, base, standing) {
    if (!standing)
        return base;
    base = resolveHftBeltSurface(world, base);
    const plain = base === null || base.kind === "normal" || base.kind === "ice";
    if (!plain)
        return base;
    const floor = activeFloor(world);
    if (floor === null)
        return base;
    if (floor.kind === "ice_floor")
        return { kind: "ice" };
    const { x, z } = heading(floor.directionDeg);
    // `applySurface` reads `speed` as an acceleration in u/s^2 and applies it for
    // one tick, so dividing the intended drift by DT makes the steady-state
    // velocity equal `driftSpeed` exactly.
    return { kind: "conveyor", dirX: x, dirZ: z, speed: floor.driftSpeed / DT };
}
export { resolveHftBeltSurface } from './hft-belts.js';
import { resolveHftBeltSurface } from './hft-belts.js';
/**
 * How far above the map's floor height still counts as standing in the lava.
 *
 * A tolerance rather than an equality because the arena deck is usually a
 * platform whose top sits AT the floor height (see `createFlatMap`), and a
 * player resting on it is a float rounding away from either side.
 */
/**
 * Lava floor damage.
 *
 * Only players at floor level burn — a raised platform is a refuge, which is
 * exactly why this mutator is worth a hovering Talon several percentage points
 * and costs a 0.85x-speed Monolith nine, and therefore why it is out of the
 * wagered pool and legal in casual lobbies only (GAME-DESIGN §4).
 *
 * Damage is credited to `ZONE_SOURCE_ID` rather than to a new "chaos" source.
 * `applyDamage` files any non-zone attacker into `aux.lastHitById`, which
 * becomes `eliminated.killerId` on the wire and `DeathRecord.killerId` in the
 * audit trail — a made-up id there would put a non-player string into both, and
 * would rob a real attacker of the kill they were three ticks away from.
 */
export function applyChaosDamage(world, events) {
    let dps = 0;
    for (const entry of world.chaos.applied) {
        const m = entry.mutation;
        if (m.kind !== "lava_floor" || !isActive(entry, world.tick))
            continue;
        if (m.dps > dps)
            dps = m.dps;
    }
    if (dps <= 0)
        return;
    // A map with no floor has nothing to set alight: the abyss below it is
    // already lethal, and `killY` handles that.
    const floorY = getMutationFloorY(world.map);
    if (floorY === undefined)
        return;
    const surfaceY = floorY + MUTATION_FLOOR_TOLERANCE;
    const report = world.tick % ZONE_HIT_EVENT_INTERVAL_TICKS === 0;
    for (const p of world.players) {
        if (p.phase !== "alive")
            continue;
        if (p.y - bodyHalfHeight(world) > surfaceY)
            continue;
        applyDamage(world, p, dps * DT, ZONE_SOURCE_ID, "chaos_lava", events, report);
    }
}
/** Unit vector for a compass heading in degrees, on the sim's deterministic trig. */
function heading(directionDeg) {
    const rad = (directionDeg * PI) / 180;
    return { x: dsin(rad), z: dcos(rad) };
}
// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------
/**
 * Canonical lines for `serializeWorld`, one per mutation ever applied.
 *
 * Parameters are read out of `MUTATION_BOUNDS` in sorted key order rather than
 * from a hand-written switch, so a parameter that exists is necessarily in the
 * checksum. A switch would let a newly added field be applied by the sim and
 * omitted from the digest, which is the one bug class this whole module exists
 * to make impossible.
 */
export function serializeChaos(state) {
    return state.applied.map((m) => `mu=${m.kind},${m.tick},${m.untilTick},${paramString(m.mutation)}`);
}
function paramString(mutation) {
    const values = mutation;
    return Object.keys(MUTATION_BOUNDS[mutation.kind])
        .sort()
        .map((field) => `${field}=${num(values[field] ?? 0)}`)
        .join(",");
}
/** Exact round-trip for a double, matching `world.ts`'s `num`. */
function num(v) {
    return Number.isFinite(v) ? v.toExponential(17) : String(v);
}
//# sourceMappingURL=mutations.js.map